import { shallowRef, watch, nextTick } from 'vue';
import type { Ref } from 'vue';

import { Modify, Draw, Snap } from 'ol/interaction';
import { createBox } from 'ol/interaction/Draw';
import { defaults as defaultInteractions } from 'ol/interaction/defaults';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import type OLMap from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type VectorSource from 'ol/source/Vector';
import type { Coordinate } from 'ol/coordinate';
import type { FeatureLike } from 'ol/Feature';

import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import MapHelpers from '@/components/map/utils/MapHelpers';
import type { LonLat } from '@/components/map/types/mapFeatureTypes';
import { getFeatureData, getFeatureType } from '@/components/map/utils/mapFeatureGuards';

import { CCM_MODE } from '@/components/map/cnetConstructorMode/ccmConsts';
import type { CcmMapContainer, CcmMapData } from '@/components/map/cnetConstructorMode/types/ccmTypes';


type XY = [number, number];

type DebouncedUpdateSource = (doFit: boolean) => void;


const toXY = (coord: Coordinate | undefined): XY => {
	const x = coord?.[0];
	const y = coord?.[1];
	return [x ?? 0, y ?? 0];
};


const sameXY = (a: XY, b: XY): boolean => (
	Math.abs(a[0] - b[0]) < 1e-10 && Math.abs(a[1] - b[1]) < 1e-10
);


const cornerToLonLat = (ring: Coordinate[] | undefined, index: number): LonLat => {
	const corner = ring?.[index];
	if (corner == null) {
		return { lon: 0, lat: 0 };
	}
	const [lon, lat] = MapHelpers.transformToLonLat(corner);
	return { lon: lon ?? 0, lat: lat ?? 0 };
};


const getGridpointLabel = (feature: FeatureLike): string | null => {
	const featureData = getFeatureData(feature);
	if (featureData == null || typeof featureData !== 'object') {
		return null;
	}
	const label = Reflect.get(featureData, 'label');
	return typeof label === 'string' ? label : null;
};


const useMapContainerCCM = (
	mapDataCCM: CcmMapData,
	debouncedUpdateSource: DebouncedUpdateSource,
	olMap: Ref<OLMap | null>,
): CcmMapContainer => {
	const sourceCCM = shallowRef<VectorSource | null>(null);

	let drawInteraction: Draw | null = null;
	let modifyInteraction: Modify | null = null;
	let snapInteraction: Snap | null = null;
	let modifyingFeature: Feature | null = null;
	let oldCoords: Coordinate[] = [];

	watch([mapDataCCM.anchorPoint, mapDataCCM.gridpoints, mapDataCCM.otherGridpoints], () => {
		debouncedUpdateSource(false);
	});

	const onDrawEnd = (): void => {
		nextTick(() => {
			const source = sourceCCM.value;
			if (source == null) {
				return;
			}
			mapDataCCM.boundRectangles.value = source.getFeatures().map(feature => {
				const geometry = feature.getGeometry();
				const ring = geometry instanceof Polygon ? geometry.getCoordinates()[0] : undefined;
				return {
					point1: cornerToLonLat(ring, 0),
					point2: cornerToLonLat(ring, 2),
				};
			});
			const firstRectangle = mapDataCCM.boundRectangles.value[0];
			if (mapDataCCM.boundRectangles.value.length === 1 && firstRectangle != null && mapDataCCM.anchorPoint.value == null) {
				mapDataCCM.anchorPoint.value = {
					lon: (firstRectangle.point1.lon + firstRectangle.point2.lon) / 2,
					lat: (firstRectangle.point1.lat + firstRectangle.point2.lat) / 2,
				};
			}
		});
	};

	const rectanglifyModifiedGeometry = (coords: Coordinate[]): Coordinate[] => {
		const pts = coords.map(toXY);
		const old = oldCoords.map(toXY);
		const get = (arr: XY[], index: number): XY => arr[index] ?? [0, 0];
		const p0 = get(pts, 0);
		const p1 = get(pts, 1);
		const p2 = get(pts, 2);
		const p3 = get(pts, 3);
		if (!sameXY(p0, get(old, 0))) {
			p3[0] = p0[0];
			p1[1] = p0[1];
		} else if (!sameXY(p1, get(old, 1))) {
			p0[1] = p1[1];
			p2[0] = p1[0];
		} else if (!sameXY(p2, get(old, 2))) {
			p1[0] = p2[0];
			p3[1] = p2[1];
		} else if (!sameXY(p3, get(old, 3))) {
			p2[1] = p3[1];
			p0[0] = p3[0];
		}
		const p4: XY = [p0[0], p0[1]];
		return [p0, p1, p2, p3, p4];
	};

	const changeFeatureFunction = (): void => {
		const feature = modifyingFeature;
		if (feature == null) {
			return;
		}
		const geometry = feature.getGeometry();
		if (!(geometry instanceof Polygon)) {
			return;
		}
		const ring = geometry.getCoordinates()[0];
		if (ring == null) {
			return;
		}
		const newCoords = rectanglifyModifiedGeometry(ring);
		geometry.setCoordinates([[...newCoords]]);
		oldCoords = newCoords;
		feature.once('change', changeFeatureFunction);
	};

	const addInteractions = (): void => {
		const map = olMap.value;
		const source = sourceCCM.value;
		if (map == null || source == null) {
			return;
		}
		if (drawInteraction != null || modifyInteraction != null || snapInteraction != null) {
			return;
		}
		modifyInteraction = new Modify({
			source,
			deleteCondition: () => false,
			insertVertexCondition: () => false,
			pixelTolerance: 5,
		});
		modifyInteraction.on('modifystart', event => {
			const feature = event.features.getArray()[0];
			if (feature == null) {
				return;
			}
			modifyingFeature = feature;
			const geometry = feature.getGeometry();
			oldCoords = geometry instanceof Polygon ? (geometry.getCoordinates()[0] ?? []) : [];
			feature.once('change', changeFeatureFunction);
		});
		modifyInteraction.on('modifyend', event => {
			const feature = event.features.getArray()[0];
			if (feature == null) {
				return;
			}
			const newFeature = feature.clone();
			source.removeFeature(feature);
			source.addFeature(newFeature);
			onDrawEnd();
		});
		map.addInteraction(modifyInteraction);
		drawInteraction = new Draw({
			source,
			type: 'Circle',
			geometryFunction: createBox(),
		});
		drawInteraction.on('drawend', onDrawEnd);
		map.addInteraction(drawInteraction);
		snapInteraction = new Snap({
			source,
			edge: false,
		});
		map.addInteraction(snapInteraction);
	};

	const removeInteractions = (): void => {
		const map = olMap.value;
		if (map == null) {
			return;
		}
		if (drawInteraction != null) {
			map.removeInteraction(drawInteraction);
			drawInteraction = null;
		}
		if (modifyInteraction != null) {
			map.removeInteraction(modifyInteraction);
			modifyInteraction = null;
		}
		if (snapInteraction != null) {
			map.removeInteraction(snapInteraction);
			snapInteraction = null;
		}
		map.getInteractions().clear();
		defaultInteractions({}).extend([]).forEach(interaction => map.addInteraction(interaction));
	};

	const onClickCnetConstructorMapMode = (event: MapBrowserEvent<UIEvent>): void => {
		const map = olMap.value;
		const source = sourceCCM.value;
		if (map == null || source == null) {
			return;
		}
		if (mapDataCCM.drawMode.value === CCM_MODE.drawRectangles) {
			const original = event.originalEvent;
			const ctrlKey = original instanceof MouseEvent ? original.ctrlKey : false;
			if (ctrlKey) {
				const clicked = map.forEachFeatureAtPixel(
					event.pixel,
					feature => feature,
					{ layerFilter: layer => layer.getSource() === source },
				);
				if (clicked instanceof Feature) {
					source.removeFeature(clicked);
					removeInteractions();
					addInteractions();
					onDrawEnd();
				}
			}
		} else if (mapDataCCM.drawMode.value === CCM_MODE.selectAnchorPoint) {
			const [lon, lat] = MapHelpers.transformToLonLat(map.getCoordinateFromPixel(event.pixel));
			if (lon != null && lat != null) {
				mapDataCCM.anchorPoint.value = { lat, lon };
			}
		} else if (mapDataCCM.drawMode.value === CCM_MODE.selectGridpoints) {
			const clicked = map.forEachFeatureAtPixel(
				event.pixel,
				feature => getFeatureType(feature) === FEATURE_TYPE.cnetConstructorGridpoint ? feature : undefined,
			);
			if (clicked != null) {
				const label = getGridpointLabel(clicked);
				if (label != null) {
					mapDataCCM.toggleGridpoint(label);
				}
			}
		}
	};

	const resetFeatures = (onlyRemove?: boolean): void => {
		const source = sourceCCM.value;
		if (source == null) {
			return;
		}
		source.getFeatures().forEach(feature => {
			source.removeFeature(feature);
		});
		removeInteractions();
		if (!onlyRemove) {
			addInteractions();
		}
	};

	const resetCnetConstructorInteractionsAndData = (): void => {
		mapDataCCM.resetCnetConstructorData(true);
		resetFeatures(true);
	};

	watch(mapDataCCM.enabled, value => {
		if (value) {
			if (mapDataCCM.drawMode.value === CCM_MODE.drawRectangles) {
				addInteractions();
			}
		} else {
			resetCnetConstructorInteractionsAndData();
		}
	});

	watch(mapDataCCM.drawMode, value => {
		if (mapDataCCM.enabled.value && value === CCM_MODE.drawRectangles) {
			addInteractions();
		} else if (value === CCM_MODE.list) {
			resetCnetConstructorInteractionsAndData();
		} else {
			removeInteractions();
		}
	});

	watch(mapDataCCM.boundRectanglesLoaded, value => {
		if (mapDataCCM.enabled.value) {
			resetFeatures(false);
			const source = sourceCCM.value;
			if (source == null) {
				return;
			}
			source.addFeatures(value.map(rectangle => new Feature({
				geometry: MapHelpers.getRectangle(rectangle),
			})));
		}
	});

	return {
		sourceCCM,
		onClickCnetConstructorMapMode,
		addInteractions,
	};
};

export { useMapContainerCCM };
